import { useState } from "react";
import { Icon, api, useAuth } from "../lib/core";

export const ProfilePage = ({ onBack, onOpenRefunds, onOpenNotifSettings }) => {
  const { user, updateUser, logout } = useAuth();
  const [activeSection, setActiveSection] = useState("profile");
  const [fullName, setFullName] = useState(user?.full_name || "");
  const [email, setEmail] = useState(user?.email || "");
  const [phone, setPhone] = useState(user?.phone || "");
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [uploadingPicture, setUploadingPicture] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });

  const handlePictureUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith("image/")) {
      setMessage({ type: "error", text: "Please select an image file" });
      return;
    }

    // Validate file size (max 500KB)
    if (file.size > 500000) {
      setMessage({ type: "error", text: "Image too large. Maximum size is 500KB" });
      return;
    }

    setUploadingPicture(true);
    setMessage({ type: "", text: "" });

    try {
      // Convert to base64
      const reader = new FileReader();
      reader.onload = async () => {
        const base64Image = reader.result;
        try {
          const response = await api.post("/user/profile-picture", {
            image: base64Image,
          });
          updateUser(response.data);
          setMessage({ type: "success", text: "Profile picture updated!" });
        } catch (err) {
          setMessage({ type: "error", text: err.response?.data?.detail || "Failed to upload picture" });
        } finally {
          setUploadingPicture(false);
        }
      };
      reader.onerror = () => {
        setMessage({ type: "error", text: "Failed to read file" });
        setUploadingPicture(false);
      };
      reader.readAsDataURL(file);
    } catch (err) {
      setMessage({ type: "error", text: "Failed to process image" });
      setUploadingPicture(false);
    }
  };

  const handleDeletePicture = async () => {
    setUploadingPicture(true);
    setMessage({ type: "", text: "" });

    try {
      await api.delete("/user/profile-picture");
      updateUser({ ...user, profile_picture: null });
      setMessage({ type: "success", text: "Profile picture removed" });
    } catch (err) {
      setMessage({ type: "error", text: err.response?.data?.detail || "Failed to remove picture" });
    } finally {
      setUploadingPicture(false);
    }
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: "", text: "" });

    try {
      const response = await api.put("/user/profile", {
        full_name: fullName,
        email: email,
        phone: phone,
      });
      updateUser(response.data);
      setMessage({ type: "success", text: "Profile updated successfully!" });
    } catch (err) {
      setMessage({ type: "error", text: err.response?.data?.detail || "Failed to update profile" });
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setMessage({ type: "", text: "" });

    if (newPassword !== confirmNewPassword) {
      setMessage({ type: "error", text: "New passwords do not match" });
      return;
    }

    if (newPassword.length < 6) {
      setMessage({ type: "error", text: "Password must be at least 6 characters" });
      return;
    }

    setLoading(true);

    try {
      await api.post("/user/change-password", {
        current_password: currentPassword,
        new_password: newPassword,
      });
      // Password change revokes old tokens; the server sets a fresh session cookie.
      setMessage({ type: "success", text: "Password changed successfully!" });
      setCurrentPassword("");
      setNewPassword("");
      setConfirmNewPassword("");
    } catch (err) {
      setMessage({ type: "error", text: err.response?.data?.detail || "Failed to change password" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background-light">
      {/* Header */}
      <header className="bg-primary-custom text-white sticky top-0 z-50">
        <div className="max-w-lg mx-auto px-4 py-4 flex items-center gap-4">
          <button onClick={onBack} className="p-2 hover:bg-white/10 rounded-lg transition-colors">
            <Icon name="arrow_back" />
          </button>
          <span className="font-bold text-xl">Profile & Settings</span>
        </div>
      </header>

      <main className="max-w-lg mx-auto px-4 py-6 space-y-6 pb-24">
        {/* Profile Card */}
        <div className="bg-white rounded-2xl p-6 text-center">
          <div className="relative inline-block">
            {user?.profile_picture ? (
              <img
                src={user.profile_picture}
                alt="Profile"
                className="w-24 h-24 rounded-full object-cover mx-auto border-4 border-primary-custom/20"
                data-testid="profile-picture"
              />
            ) : (
              <div className="w-24 h-24 bg-primary-custom/10 rounded-full flex items-center justify-center mx-auto border-4 border-primary-custom/20">
                <Icon name="person" className="text-primary-custom text-5xl" />
              </div>
            )}
            {/* Upload Button Overlay */}
            <label
              data-testid="upload-picture-btn"
              className="absolute bottom-0 right-0 w-8 h-8 bg-primary-custom text-white rounded-full flex items-center justify-center cursor-pointer hover:bg-primary-custom/90 transition-colors shadow-lg"
            >
              <Icon name="camera_alt" className="text-sm" />
              <input
                type="file"
                accept="image/*"
                onChange={handlePictureUpload}
                className="hidden"
                disabled={uploadingPicture}
              />
            </label>
          </div>
          {uploadingPicture && (
            <div className="mt-2 flex items-center justify-center gap-2 text-primary-custom text-sm">
              <Icon name="progress_activity" className="animate-spin text-base" />
              <span>Uploading...</span>
            </div>
          )}
          {user?.profile_picture && !uploadingPicture && (
            <button
              onClick={handleDeletePicture}
              data-testid="delete-picture-btn"
              className="mt-2 text-red-500 text-sm flex items-center justify-center gap-1 mx-auto hover:underline"
            >
              <Icon name="delete" className="text-base" />
              <span>Remove photo</span>
            </button>
          )}
          <h2 className="font-bold text-xl text-slate-900 mt-4">{user?.full_name}</h2>
          <p className="text-slate-500">{user?.email}</p>
          <p className="text-slate-400 text-sm mt-1">{user?.phone}</p>
        </div>

        {/* Section Tabs */}
        <div className="flex bg-white rounded-xl p-1.5 gap-1">
          <button
            onClick={() => setActiveSection("profile")}
            className={`flex-1 py-3 rounded-lg font-semibold transition-all flex items-center justify-center gap-2 ${
              activeSection === "profile"
                ? "bg-primary-custom text-white"
                : "text-slate-600 hover:bg-slate-100"
            }`}
          >
            <Icon name="edit" />
            <span>Edit Profile</span>
          </button>
          <button
            onClick={() => setActiveSection("security")}
            className={`flex-1 py-3 rounded-lg font-semibold transition-all flex items-center justify-center gap-2 ${
              activeSection === "security"
                ? "bg-primary-custom text-white"
                : "text-slate-600 hover:bg-slate-100"
            }`}
          >
            <Icon name="security" />
            <span>Security</span>
          </button>
        </div>

        {/* Message */}
        {message.text && (
          <div className={`px-4 py-3 rounded-xl flex items-center gap-2 ${
            message.type === "success" 
              ? "bg-green-50 border border-green-200 text-green-700" 
              : "bg-red-50 border border-red-200 text-red-700"
          }`}>
            <Icon name={message.type === "success" ? "check_circle" : "error"} />
            <span>{message.text}</span>
          </div>
        )}

        {activeSection === "profile" ? (
          /* Edit Profile Form */
          <form onSubmit={handleUpdateProfile} className="bg-white rounded-2xl p-6 space-y-4">
            <div className="flex flex-col gap-1.5">
              <label className="text-slate-700 text-sm font-semibold">Full Name</label>
              <div className="relative flex items-center">
                <Icon name="badge" className="absolute left-4 text-slate-400" />
                <input
                  type="text"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  className="flex w-full rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:border-primary-custom focus:ring-2 focus:ring-primary-custom/20 h-14 pl-12 pr-4 text-base outline-none transition-all"
                />
              </div>
            </div>

            <div className="flex flex-col gap-1.5">
              <label className="text-slate-700 text-sm font-semibold">Email Address</label>
              <div className="relative flex items-center">
                <Icon name="mail" className="absolute left-4 text-slate-400" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="flex w-full rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:border-primary-custom focus:ring-2 focus:ring-primary-custom/20 h-14 pl-12 pr-4 text-base outline-none transition-all"
                />
              </div>
            </div>

            <div className="flex flex-col gap-1.5">
              <label className="text-slate-700 text-sm font-semibold">Phone Number</label>
              <div className="relative flex items-center">
                <Icon name="phone" className="absolute left-4 text-slate-400" />
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className="flex w-full rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:border-primary-custom focus:ring-2 focus:ring-primary-custom/20 h-14 pl-12 pr-4 text-base outline-none transition-all"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full h-14 bg-primary-custom hover:bg-primary-custom/90 text-white rounded-xl font-bold text-lg shadow-lg shadow-primary-custom/20 transition-all flex items-center justify-center gap-2 disabled:opacity-50 mt-6"
            >
              {loading ? (
                <Icon name="progress_activity" className="animate-spin" />
              ) : (
                <>
                  <Icon name="save" />
                  <span>Save Changes</span>
                </>
              )}
            </button>
          </form>
        ) : (
          /* Security Form */
          <form onSubmit={handleChangePassword} className="bg-white rounded-2xl p-6 space-y-4">
            <h3 className="font-bold text-slate-900 mb-4">Change Password</h3>
            
            <div className="flex flex-col gap-1.5">
              <label className="text-slate-700 text-sm font-semibold">Current Password</label>
              <div className="relative flex items-center">
                <Icon name="lock" className="absolute left-4 text-slate-400" />
                <input
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  className="flex w-full rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:border-primary-custom focus:ring-2 focus:ring-primary-custom/20 h-14 pl-12 pr-4 text-base outline-none transition-all"
                  placeholder="Enter current password"
                  required
                />
              </div>
            </div>

            <div className="flex flex-col gap-1.5">
              <label className="text-slate-700 text-sm font-semibold">New Password</label>
              <div className="relative flex items-center">
                <Icon name="lock" className="absolute left-4 text-slate-400" />
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="flex w-full rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:border-primary-custom focus:ring-2 focus:ring-primary-custom/20 h-14 pl-12 pr-4 text-base outline-none transition-all"
                  placeholder="Enter new password"
                  required
                />
              </div>
            </div>

            <div className="flex flex-col gap-1.5">
              <label className="text-slate-700 text-sm font-semibold">Confirm New Password</label>
              <div className="relative flex items-center">
                <Icon name="lock" className="absolute left-4 text-slate-400" />
                <input
                  type="password"
                  value={confirmNewPassword}
                  onChange={(e) => setConfirmNewPassword(e.target.value)}
                  className="flex w-full rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:border-primary-custom focus:ring-2 focus:ring-primary-custom/20 h-14 pl-12 pr-4 text-base outline-none transition-all"
                  placeholder="Confirm new password"
                  required
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full h-14 bg-primary-custom hover:bg-primary-custom/90 text-white rounded-xl font-bold text-lg shadow-lg shadow-primary-custom/20 transition-all flex items-center justify-center gap-2 disabled:opacity-50 mt-6"
            >
              {loading ? (
                <Icon name="progress_activity" className="animate-spin" />
              ) : (
                <>
                  <Icon name="key" />
                  <span>Change Password</span>
                </>
              )}
            </button>
          </form>
        )}

        {/* Notification settings entry */}
        <button
          onClick={onOpenNotifSettings}
          data-testid="profile-notifications-btn"
          className="w-full h-14 bg-white border border-slate-200 text-slate-700 hover:bg-slate-50 rounded-xl font-semibold transition-all flex items-center justify-center gap-2"
        >
          <Icon name="notifications" className="text-primary-custom" />
          <span>Notification settings</span>
        </button>

        {/* Refunds entry */}
        <button
          onClick={onOpenRefunds}
          data-testid="profile-refunds-btn"
          className="w-full h-14 bg-white border border-slate-200 text-slate-700 hover:bg-slate-50 rounded-xl font-semibold transition-all flex items-center justify-center gap-2"
        >
          <Icon name="currency_exchange" className="text-primary-custom" />
          <span>Request a refund</span>
        </button>

        {/* Logout Button */}
        <button
          onClick={logout}
          className="w-full h-14 bg-white border-2 border-red-500 text-red-500 hover:bg-red-50 rounded-xl font-bold text-lg transition-all flex items-center justify-center gap-2"
        >
          <Icon name="logout" />
          <span>Log Out</span>
        </button>
      </main>
    </div>
  );
};

// Provider Card Component
